import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
  },
  cardTitleContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: 600,
    color: '#111827',
    justifyContent: 'flex-start',
  },
  closeButton: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  dialogContent: {
    margin: theme.spacing(0, 3, 3, 3),
    padding: 0,
  },
  stepsList: {
    paddingLeft: 0,
    marginTop: theme.spacing(2),
    listStyleType: 'none',
    '& li': {
      marginBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(3),
      position: 'relative',
      lineHeight: 1.5,
      '&:before': {
        content: 'counter(step-counter)',
        counterIncrement: 'step-counter',
        position: 'absolute',
        left: 0,
        top: 0,
        width: theme.spacing(2.5),
        height: theme.spacing(2.5),
        borderRadius: '50%',
        backgroundColor: '#f3f4f6',
        color: '#374151',
        fontSize: '0.875rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  },
  warningBox: {
    backgroundColor: '#FEF3C7',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  warningIcon: {
    color: '#D97706',
    fontSize: '1.2rem',
    marginTop: '2px',
  },
  warningText: {
    color: '#555',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  marketplaceButton: {
    backgroundColor: '#FF9500',
    color: '#FFFFFF',
    fontWeight: 600,
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    marginTop: theme.spacing(3),
    '&:hover': {
      backgroundColor: '#E6850E',
    },
  },
}));

export interface AwsMarketplaceLicenseDialogProps {
  open: boolean;
  onClose: () => void;
}

const AwsMarketplaceLicenseDialog = ({ open, onClose }: AwsMarketplaceLicenseDialogProps) => {
  const classes = useStyles();

  const handleGoToMarketplace = () => {
    window.open(process.env.REACT_APP_AWS_MARKETPLACE_URL, '_blank');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 560,
        },
      }}
    >
      <div className={classes.card}>
        <div className={classes.cardTitleContent}>
          <DialogTitle className={classes.title}>
            <FormattedMessage
              id="page.subscription.aws.marketplace.dialog.title"
            />
          </DialogTitle>
          <IconButton
            className={classes.closeButton}
            aria-label="close"
            onClick={onClose}
            edge="end"
            size="small"
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </div>
      </div>

      <DialogContent className={classes.dialogContent}>
        <Typography variant="body1" color="#555" fontWeight={600} sx={{ marginBottom: 2 }}>
          <FormattedMessage
            id="page.subscription.aws.marketplace.dialog.description"
          />
        </Typography>

        <Typography
          variant="h6"
          fontWeight={600}
          color="#111827"
          sx={{ marginBottom: 1, fontSize: '1.1rem' }}
        >
          <FormattedMessage
            id="page.subscription.aws.marketplace.dialog.quick.steps"
          />
        </Typography>

        <Box component="ol" className={classes.stepsList} sx={{ counterReset: 'step-counter' }}>
          <li>
            <Typography variant="body2" color="#374151">
              <FormattedMessage
                id="page.subscription.aws.marketplace.dialog.step1"
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="#374151">
              <FormattedMessage
                id="page.subscription.aws.marketplace.dialog.step2"
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="#374151">
              <FormattedMessage
                id="page.subscription.aws.marketplace.dialog.step3"
              />
            </Typography>
          </li>
        </Box>

        <Box className={classes.warningBox}>
          <Typography className={classes.warningIcon}>💡</Typography>
          <Typography className={classes.warningText}>
            <FormattedMessage
              id="page.subscription.aws.marketplace.dialog.warning"
            />
          </Typography>
        </Box>

        <Box display="flex" justifyContent="flex-start">
          <Button
            className={classes.marketplaceButton}
            onClick={handleGoToMarketplace}
            variant="contained"
          >
            <FormattedMessage
              id="page.subscription.aws.marketplace.dialog.button"
            />
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AwsMarketplaceLicenseDialog;
